import React, { useMemo, useState } from 'react'

import { Stack, Box, Chip } from '@mui/material'
import { UsersIcon, OctagonPauseIcon, CircleCheckIcon } from 'lucide-react'
import { useTheme } from '@mui/material'

import { EmptyState, Table, StatusChipCell, ActionDropdown } from 'components'
import { useCompanyUsersQuery, useCompanyMutationsQuery } from 'hooks/api'
import { useSnackbar } from 'contexts'
import { getErrorMessage } from 'utils'
import { ROLES } from 'constants/roles'
import { palette } from 'constants/theme'
import type { CompanyUser } from 'types'

import { SuspendMemberModal } from './SuspendMemberModal'

// StatusChip expects real CSS colors (it feeds them to lighten()/alpha() for the
// dark-mode tint) — not MUI Chip semantic names like 'success'/'default', which
// throw "Unsupported color" in dark mode. Mirror the palette convention used by
// the shared maps in constants/statuses.ts.
const INVITATION_STATUSES = {
  active: { label: 'Active', color: palette.green[700], borderColor: palette.green[200], backgroundColor: palette.green[100] },
  invited: { label: 'Invited', color: palette.orange[700], borderColor: palette.orange[200], backgroundColor: palette.orange[100] },
  pending: { label: 'Pending', color: palette.gray[700], borderColor: palette.gray[200], backgroundColor: palette.gray[100] },
} as const

interface CompanyMembersPanelProps {
  companyId: string
  companyName?: string
}

export const CompanyMembersPanel = ({
  companyId,
  companyName,
}: CompanyMembersPanelProps) => {
  const theme = useTheme()
  const { open: notification } = useSnackbar()

  const [suspendTarget, setSuspendTarget] = useState<CompanyUser | null>(null)
  const [suspendModalOpen, setSuspendModalOpen] = useState(false)

  const { data, isLoading, refetch } = useCompanyUsersQuery(
    { id: companyId },
    { enabled: !!companyId },
  )

  const members = data?.data ?? []

  const { unsuspendMember } = useCompanyMutationsQuery({
    onSuccess: () => {
      refetch()
    },
    onError: (error) => {
      notification({ type: 'error', title: getErrorMessage(error) })
    },
  })

  const handleSuspend = (member: CompanyUser) => {
    setSuspendTarget(member)
    setSuspendModalOpen(true)
  }

  const handleUnsuspend = (member: CompanyUser) => {
    unsuspendMember({ companyId, userId: member.id })
  }

  const columns = useMemo(
    () => [
      {
        field: 'name',
        headerName: 'Name',
        type: 'string',
        width: 200,
        // Return a plain string (like Email/Role) so the cell uses the grid's
        // default vertical-centering; a Typography child top-aligned in the row.
        valueGetter: (_value: unknown, row: CompanyUser) =>
          `${row.first_name ?? ''} ${row.last_name ?? ''}`.trim(),
      },
      {
        field: 'email',
        headerName: 'Email',
        type: 'string',
        flex: 1,
        minWidth: 200,
        renderCell: ({ row }: { row: CompanyUser }) => row.email,
      },
      {
        field: 'role',
        headerName: 'Role',
        type: 'string',
        width: 130,
        renderCell: ({ row }: { row: CompanyUser }) =>
          ROLES[row.role?.name as keyof typeof ROLES] ?? row.role?.name ?? '—',
      },
      {
        field: 'status',
        headerName: 'Status',
        type: 'string',
        width: 160,
        renderCell: ({ row }: { row: CompanyUser }) => {
          const statusConfig =
            INVITATION_STATUSES[
              row.invitation_status as keyof typeof INVITATION_STATUSES
            ]
          return (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ height: '100%' }}>
              <StatusChipCell
                status={row.invitation_status}
                statuses={Object.fromEntries(
                  Object.entries(INVITATION_STATUSES).map(([k, v]) => [k, { ...v }]),
                )}
              />
              {row.suspended && (
                <Chip
                  label="Suspended"
                  size="small"
                  sx={{
                    bgcolor: theme.palette.error.main + '22',
                    color: theme.palette.error.main,
                    fontWeight: 500,
                    fontSize: 11,
                    height: 20,
                  }}
                />
              )}
            </Stack>
          )
        },
      },
      {
        field: 'actions',
        headerName: false,
        width: 64,
        align: 'right' as const,
        sortable: false,
        // Row actions as a kebab — consistent with the other internal tables
        // (e.g. CompaniesPage) and the kebab↔detail-parity convention.
        renderCell: ({ row }: { row: CompanyUser }) => (
          <ActionDropdown
            items={[
              row.suspended
                ? {
                    label: 'Unsuspend',
                    onClick: () => handleUnsuspend(row),
                    icon: <CircleCheckIcon size={16} strokeWidth={1} />,
                  }
                : {
                    label: 'Suspend',
                    onClick: () => handleSuspend(row),
                    destructive: true,
                    icon: (
                      <OctagonPauseIcon
                        size={16}
                        strokeWidth={1}
                        color={theme.palette.error.main}
                      />
                    ),
                  },
            ]}
          />
        ),
      },
    ],
    [theme, companyId],
  )

  const rows = useMemo(
    () => members.map((m) => ({ ...m, id: m.id })),
    [members],
  )

  return (
    <Box>
      <Table
        fillBottom
        loading={isLoading}
        topOffset={360}
        columns={columns}
        rows={rows}
        rowCount={rows.length}
        emptyState={
          <EmptyState
            title="No members"
            text="This company has no members yet."
            icon={<UsersIcon strokeWidth={1} size={32} />}
          />
        }
      />

      <SuspendMemberModal
        member={suspendTarget}
        companyId={companyId}
        companyName={companyName}
        open={suspendModalOpen}
        onClose={() => {
          setSuspendModalOpen(false)
          setSuspendTarget(null)
        }}
        onSuccess={() => {
          refetch()
        }}
      />
    </Box>
  )
}
